import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Save, Upload, Trash2, Image as ImageIcon, FileText, Loader2 } from "lucide-react";

export default function ImmigrationRetainerSettings() {
  const [s, setS] = useState({});
  const [name, setName] = useState("");
  const [license, setLicense] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const sigRef = useRef(); const initRef = useRef(); const tplRef = useRef();

  const load = async () => {
    const r = await api.get("/immigration/firm-settings");
    setS(r.data || {});
    setName(r.data?.rcic_name || ""); setLicense(r.data?.rcic_license || ""); setTitle(r.data?.rcic_title || "");
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try { await api.patch("/immigration/firm-settings", { rcic_name: name, rcic_license: license, rcic_title: title }); toast.success("Saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const uploadImg = async (ref, kind) => {
    const f = ref.current?.files?.[0]; if (!f) return toast.error("Pick a PNG/JPG");
    setBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/immigration/firm-settings/signature?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind} uploaded`); ref.current.value = ""; await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const uploadTpl = async () => {
    const f = tplRef.current?.files?.[0]; if (!f) return toast.error("Pick a PDF");
    setBusy("template");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/immigration/firm-settings/retainer-template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded · ${r.data.pages} pages`); tplRef.current.value = ""; await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const removeTpl = async () => {
    if (!window.confirm("Delete the retainer template? New retainers will fall back to the built-in template.")) return;
    try { await api.delete("/immigration/firm-settings/retainer-template"); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ImgCard = ({ title, src, name, kind, ref: imgRef }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-slate-400"/>{title}</div>
      </div>
      <div className="h-32 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
        {src ? <img src={src} alt={title} className="max-h-full max-w-full object-contain"/> : <span className="text-xs text-slate-400">No image uploaded</span>}
      </div>
      <div className="flex items-center gap-2">
        <input ref={imgRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="text-xs flex-1" data-testid={`firm-${kind}-input`}/>
        <Button size="sm" onClick={() => uploadImg(imgRef, kind)} disabled={busy === kind} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid={`firm-${kind}-upload`}>
          {busy === kind ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/> : <Upload className="w-3 h-3 mr-1"/>}Upload
        </Button>
      </div>
      <p className="text-[11px] text-slate-500">Transparent PNG works best. Max 2 MB.</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Retainer & RCIC Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Configure the firm's CICC-licensed representative, upload the signature/initials used to auto counter-sign retainers,
          and attach your firm's retainer template PDF. Every signature is timestamped, IP-logged, and fingerprinted (SHA-256)
          to be admissible under Canada's Electronic Commerce Act &amp; CICC record-keeping rules.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="text-xs uppercase text-slate-500">Representative Profile</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label className="text-xs text-slate-600">RCIC Full Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jane Doe" data-testid="firm-rcic-name"/></div>
          <div><Label className="text-xs text-slate-600">CICC License #</Label>
            <Input value={license} onChange={e => setLicense(e.target.value)} placeholder="e.g. R123456" data-testid="firm-rcic-license"/></div>
          <div><Label className="text-xs text-slate-600">Title (optional)</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Managing Director, RCIC" data-testid="firm-rcic-title"/></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={saveProfile} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="firm-save-profile">
            <Save className="w-4 h-4 mr-1"/>{saving ? "Saving…" : "Save Profile"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ImgCard title="RCIC Signature" src={s.rcic_signature_png} kind="signature" ref={sigRef}/>
        <ImgCard title="RCIC Initials" src={s.rcic_initials_png} kind="initials" ref={initRef}/>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400"/>Retainer Template PDF</div>
            <div className="text-xs text-slate-500 mt-0.5">Upload your firm's retainer agreement. The signing/audit page is auto-appended with case data, fees, and signatures.</div>
            {s.retainer_template_name && <div className="text-xs text-emerald-700 mt-2">✓ <span className="mono">{s.retainer_template_name}</span> · {s.retainer_template_pages} pages</div>}
          </div>
          <div className="flex gap-2">
            {s.retainer_template_name && <Button variant="outline" onClick={removeTpl} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="firm-tpl-remove"><Trash2 className="w-4 h-4 mr-1"/>Remove</Button>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={tplRef} type="file" accept=".pdf" className="text-sm flex-1 min-w-[200px]" data-testid="firm-tpl-input"/>
          <Button onClick={uploadTpl} disabled={busy === "template"} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="firm-tpl-upload">
            {busy === "template" ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Upload className="w-4 h-4 mr-1"/>}
            {s.retainer_template_name ? "Replace" : "Upload Template"}
          </Button>
        </div>
      </div>
    </div>
  );
}
